// =============================================================================
// features/relatorios/comercial/components/ComercialSection.tsx — Aba
// Comercial de /relatorios (F35-S12, doc 26 §16).
//
// Gate DUPLO (mesmo padrão de ComercialSettingsPage.tsx):
//   1. `sales_reports:read` — sem ela, estado próprio (nunca tela quebrada).
//   2. Flag `sales.reports.enabled` — some silenciosamente enquanto piloto.
// O conteúdo real (ComercialContent) só monta depois dos dois gates.
//
// DoD do slot:
//   - Definição de cada métrica exibida na UI → <MetricLabel> com tooltip
//     (title nativo) alimentado por `data.definitions`.
//   - Gráfico tem tabela equivalente acessível → toda barra em
//     <AttemptsBreakdown> tem uma <table> irmã (visualmente compacta, mas
//     lida por leitor de tela e testável).
//   - Dados stale identificados → <FreshnessIndicator> (mesmo componente do
//     restante de /relatorios).
//   - Export respeita `reports:export` → botão só aparece com a permissão.
//   - Duração de ligação NUNCA aparece como indicador — não existe no
//     response (ver packages/shared-schemas/src/sales-reports.ts).
// =============================================================================
import type { SalesReportsOverviewResponse, SalesReportsQuery } from '@elemento/shared-schemas';
import * as React from 'react';

import { FreshnessIndicator } from '../../../../components/ui/FreshnessIndicator';
import { Stat } from '../../../../components/ui/Stat';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useAuth } from '../../../auth/useAuth';
import { useSalesReportsExport } from '../hooks/useSalesReportsExport';
import { useSalesReportsOverview } from '../hooks/useSalesReportsOverview';

const FLAG_KEY = 'sales.reports.enabled';
const READ_PERMISSION = 'sales_reports:read';
const EXPORT_PERMISSION = 'reports:export';

function fmtNumber(n: number): string {
  return n.toLocaleString('pt-BR');
}
function fmtPercent(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%';
}
function fmtMinutes(n: number | null): string {
  if (n === null) return '—';
  if (n < 60) return Math.round(n).toLocaleString('pt-BR') + ' min';
  const hours = n / 60;
  if (hours < 24) return hours.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' h';
  return (hours / 24).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' d';
}

// ---------------------------------------------------------------------------
// Definição de métrica (DoD: "definição de cada métrica exibida na UI").
//
// `Stat` (componente canônico do DS, fora de `files_allowed` deste slot —
// não pode ser modificado) já tem um slot de `description` renderizado como
// legenda abaixo do valor. Reaproveitamos esse slot para a definição vinda
// do backend em vez de forçar um nó React dentro de `label` (que é tipado
// `string`) — evita um `as` sem necessidade real.
// ---------------------------------------------------------------------------

function statProps(
  label: string,
  definitionKey: string,
  definitions: Record<string, string>,
): { label: string; description?: string } {
  const description = definitions[definitionKey];
  return description === undefined ? { label } : { label, description };
}

// ---------------------------------------------------------------------------
// Estados de carregamento/erro/vazio
// ---------------------------------------------------------------------------

function ComercialSkeleton(): React.JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="rounded-md border border-border bg-surface-1 p-5 animate-pulse"
          style={{ boxShadow: 'var(--elev-2)', minHeight: '100px' }}
        />
      ))}
    </div>
  );
}

function ComercialError({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  return (
    <div
      className="flex flex-col items-center gap-4 rounded-md border px-6 py-10 text-center"
      style={{ borderColor: 'var(--border)', background: 'var(--danger-bg)' }}
    >
      <p className="font-sans text-sm text-ink-2">
        Não foi possível carregar os indicadores comerciais.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="font-sans text-sm font-semibold rounded-sm px-4 py-2"
        style={{
          background: 'var(--surface-1)',
          border: '1px solid var(--border-strong)',
          color: 'var(--text)',
          boxShadow: 'var(--elev-1)',
        }}
      >
        Tentar novamente
      </button>
    </div>
  );
}

function ComercialForbidden(): React.JSX.Element {
  return (
    <div
      className="rounded-md border px-6 py-8 text-center"
      style={{ borderColor: 'var(--border)', background: 'var(--surface-1)' }}
    >
      <p className="font-sans text-sm text-ink-3">
        Você não tem permissão para visualizar os relatórios comerciais.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breakdown de tentativas — barra + tabela equivalente (DoD).
// ---------------------------------------------------------------------------

function AttemptsBreakdown({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ key: string; label: string; count: number }>;
}): React.JSX.Element {
  if (rows.length === 0) {
    return (
      <div>
        <h3 className="font-sans text-xs font-semibold text-ink-3 uppercase tracking-wide mb-2">
          {title}
        </h3>
        <p className="font-sans text-xs text-ink-3">Sem tentativas no período.</p>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.count));
  return (
    <div>
      <h3 className="font-sans text-xs font-semibold text-ink-3 uppercase tracking-wide mb-2">
        {title}
      </h3>
      {/* Gráfico — barras horizontais, decorativo (aria-hidden: a tabela abaixo é a versão acessível) */}
      <div className="space-y-1.5 mb-3" aria-hidden="true">
        {rows.map((row) => (
          <div key={row.key} className="flex items-center gap-2">
            <span className="font-sans text-xs text-ink-2 w-32 truncate flex-shrink-0">
              {row.label}
            </span>
            <div
              className="flex-1 rounded-full overflow-hidden"
              style={{ height: '6px', background: 'var(--surface-muted)' }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${max > 0 ? (row.count / max) * 100 : 0}%`,
                  background: 'var(--brand)',
                }}
              />
            </div>
            <span className="font-mono text-xs text-ink-3 w-10 text-right flex-shrink-0">
              {fmtNumber(row.count)}
            </span>
          </div>
        ))}
      </div>
      {/* Tabela equivalente acessível (DoD: "gráfico tem tabela equivalente acessível") —
          a barra acima é aria-hidden; esta <table> é a leitura primária para
          leitor de tela e navegação por teclado. <details> mantém a UI compacta
          por padrão sem esconder o conteúdo de tecnologia assistiva. */}
      <details>
        <summary className="font-sans text-xs text-ink-3 cursor-pointer select-none">
          Ver como tabela
        </summary>
        <table className="w-full text-left mt-2">
          <caption className="sr-only">{title}</caption>
          <thead>
            <tr>
              <th scope="col" className="font-sans text-xs text-ink-3 pr-4">
                Item
              </th>
              <th scope="col" className="font-sans text-xs text-ink-3">
                Tentativas
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td className="font-sans text-xs text-ink-2 pr-4">{row.label}</td>
                <td className="font-mono text-xs text-ink-3">{fmtNumber(row.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Conteúdo
// ---------------------------------------------------------------------------

function ComercialContent({
  data,
  isFetching,
  updatedAt,
  onRefresh,
  canExport,
  onExport,
  isExporting,
}: {
  data: SalesReportsOverviewResponse;
  isFetching: boolean;
  updatedAt: number;
  onRefresh: () => void;
  canExport: boolean;
  onExport: () => void;
  isExporting: boolean;
}): React.JSX.Element {
  const def = data.definitions;

  const byAgentRows = data.attempts.byAgent.map((r) => ({
    key: r.agentId,
    label: r.agentName,
    count: r.count,
  }));
  const byCampaignRows = data.attempts.byCampaign.map((r) => ({
    key: r.campaignId,
    label: r.campaignName,
    count: r.count,
  }));
  const byChannelRows = data.attempts.byChannel.map((r) => ({
    key: r.channel,
    label: r.channel,
    count: r.count,
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FreshnessIndicator
          updatedAt={updatedAt}
          isFetching={isFetching}
          onRefresh={onRefresh}
          refreshedAt={data.stale.asOf}
          timeZone={data.range.timeZone}
        />
        {canExport && (
          <button
            type="button"
            onClick={onExport}
            disabled={isExporting}
            className="font-sans text-xs font-semibold rounded-xs px-3 py-1.5 border disabled:opacity-50"
            style={{
              borderColor: 'var(--border-strong)',
              color: 'var(--text-3)',
              background: 'var(--bg-elev-1)',
            }}
          >
            {isExporting ? 'Exportando…' : 'Exportar CSV'}
          </button>
        )}
      </div>

      {/* Carteira */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Stat
          {...statProps('Carteira total', 'wallet.total', def)}
          value={fmtNumber(data.wallet.total)}
        />
        <Stat
          {...statProps('Disponível', 'wallet.available', def)}
          value={fmtNumber(data.wallet.available)}
        />
        <Stat
          {...statProps('Em retorno', 'wallet.inRetorno', def)}
          value={fmtNumber(data.wallet.inRetorno)}
        />
        <Stat
          {...statProps('Concluída', 'wallet.completed', def)}
          value={fmtNumber(data.wallet.completed)}
        />
        <Stat
          {...statProps('Bloqueada', 'wallet.blocked', def)}
          value={fmtNumber(data.wallet.blocked)}
        />
      </div>

      {/* Taxas */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          {...statProps('Taxa de contato', 'rates.contact', def)}
          value={fmtPercent(data.rates.contact)}
        />
        <Stat
          {...statProps('Taxa de interesse', 'rates.interest', def)}
          value={fmtPercent(data.rates.interest)}
        />
        <Stat
          {...statProps('Taxa de proposta', 'rates.proposal', def)}
          value={fmtPercent(data.rates.proposal)}
        />
        <Stat
          {...statProps('Taxa de conversão', 'rates.conversion', def)}
          value={fmtPercent(data.rates.conversion)}
        />
      </div>

      {/* Tempo até primeiro contato + retornos */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
        <Stat
          {...statProps('Tempo médio até 1º contato', 'timeToFirstContact.avgMinutes', def)}
          value={fmtMinutes(data.timeToFirstContact.avgMinutes)}
        />
        <Stat
          {...statProps('Tempo mediano até 1º contato', 'timeToFirstContact.medianMinutes', def)}
          value={fmtMinutes(data.timeToFirstContact.medianMinutes)}
        />
        <Stat
          {...statProps('Retornos marcados', 'callbacks.marked', def)}
          value={fmtNumber(data.callbacks.marked)}
        />
        <Stat
          {...statProps('Retornos atendidos', 'callbacks.attended', def)}
          value={fmtNumber(data.callbacks.attended)}
        />
        <Stat
          {...statProps('Retornos vencidos', 'callbacks.overdue', def)}
          value={fmtNumber(data.callbacks.overdue)}
        />
        <Stat
          {...statProps('Reagendados', 'callbacks.rescheduled', def)}
          value={fmtNumber(data.callbacks.rescheduled)}
        />
      </div>

      {/* Funil */}
      <div>
        <h3 className="font-sans text-xs font-semibold text-ink-3 uppercase tracking-wide mb-2">
          Funil da carteira
        </h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat
            {...statProps('Importados', 'funnel.imported', def)}
            value={fmtNumber(data.funnel.imported)}
          />
          <Stat
            {...statProps('Trabalhados', 'funnel.worked', def)}
            value={fmtNumber(data.funnel.worked)}
          />
          <Stat
            {...statProps('Contatados', 'funnel.contacted', def)}
            value={fmtNumber(data.funnel.contacted)}
          />
          <Stat
            {...statProps('Convertidos', 'funnel.converted', def)}
            value={fmtNumber(data.funnel.converted)}
          />
        </div>
      </div>

      {/* Tentativas — breakdowns */}
      <div>
        <h3 className="font-sans text-sm font-semibold text-ink mb-3">
          Tentativas de contato ({fmtNumber(data.attempts.total)} no período)
        </h3>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <AttemptsBreakdown title="Por vendedor" rows={byAgentRows} />
          <AttemptsBreakdown title="Por campanha" rows={byCampaignRows} />
          <AttemptsBreakdown title="Por canal" rows={byChannelRows} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente exportado — gate de permissão + flag
// ---------------------------------------------------------------------------

export interface ComercialSectionProps {
  query: Partial<SalesReportsQuery>;
}

export function ComercialSection({ query }: ComercialSectionProps): React.JSX.Element | null {
  const { hasPermission } = useAuth();
  const { enabled: flagEnabled, isLoading: flagLoading } = useFeatureFlag(FLAG_KEY);
  const canRead = hasPermission(READ_PERMISSION);
  const canExport = canRead && hasPermission(EXPORT_PERMISSION);

  const { data, isLoading, isFetching, updatedAt, isError, isForbidden, refetch } =
    useSalesReportsOverview(canRead && flagEnabled ? query : {});
  const { exportReport, isExporting } = useSalesReportsExport();

  // Sem a permissão OU flag desligada: a aba inteira some — o piloto é
  // por convite, não uma tela "sem acesso" espalhada pela página inteira.
  if (flagLoading) return null;
  if (!canRead || !flagEnabled) return null;

  if (isForbidden) return <ComercialForbidden />;
  if (isLoading) return <ComercialSkeleton />;
  if (isError || !data) return <ComercialError onRetry={refetch} />;

  return (
    <ComercialContent
      data={data}
      isFetching={isFetching}
      updatedAt={updatedAt}
      onRefresh={refetch}
      canExport={canExport}
      onExport={() => exportReport({ ...query, range: query.range ?? 'last30d', format: 'csv' })}
      isExporting={isExporting}
    />
  );
}
